
import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageTitle } from "@/components/ui/PageTitle";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EggProductionForm } from "@/components/egg-production/EggProductionForm";
import { EggProductionList } from "@/components/egg-production/EggProductionList";
import { EggProductionStats } from "@/components/egg-production/EggProductionStats";
import { useOwnerData } from "@/hooks/useOwnerData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { EmptyPlaceholder } from "@/components/interface/EmptyPlaceholder";

const EggProduction = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [stockItemId, setStockItemId] = useState<string | null>(null);
  const { data: ownerData } = useOwnerData();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStockItemId = async () => {
      if (!ownerData?.business_type_id) return;

      try {
        const { data: businessStock } = await supabase
          .from('business_stocks')
          .select('id')
          .eq('business_id', ownerData.business_type_id)
          .single();

        if (!businessStock) {
          setIsLoading(false);
          return;
        }

        const { data: stockItems } = await supabase
          .from('stock_items')
          .select('id')
          .eq('stock_id', businessStock.id)
          .limit(1);

        if (stockItems && stockItems.length > 0) {
          setStockItemId(stockItems[0].id);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Erreur lors de la récupération du stock:", error);
        toast.error("Erreur lors du chargement des données");
        setIsLoading(false);
      }
    };

    fetchStockItemId();
  }, [ownerData]);

  if (isLoading) {
    return (
      <DashboardLayout userType="owner">
        <div className="space-y-6">
          <PageTitle
            title="Production d'œufs"
            subtitle="Gérez la production d'œufs de votre ferme"
          />
          <p className="text-center py-8">Chargement...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!stockItemId) {
    return (
      <DashboardLayout userType="owner">
        <div className="space-y-6">
          <PageTitle
            title="Production d'œufs"
            subtitle="Gérez la production d'œufs de votre ferme"
          />
          <EmptyPlaceholder>
            <EmptyPlaceholder.Icon name="egg" />
            <EmptyPlaceholder.Title>Aucun lot de volailles</EmptyPlaceholder.Title>
            <EmptyPlaceholder.Description>
              Vous devez d'abord créer un lot de volailles dans votre stock avant de pouvoir enregistrer la production d'œufs.
            </EmptyPlaceholder.Description>
          </EmptyPlaceholder>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="owner">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <PageTitle
            title="Production d'œufs"
            subtitle="Gérez la production d'œufs de votre ferme"
          />
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle production
          </Button>
        </div>

        <Card className="p-6 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-4">
            <AlertTriangle className="h-6 w-6 text-yellow-500" />
            <div>
              <h3 className="font-medium">En développement</h3>
              <p className="text-sm text-muted-foreground">
                Des fonctionnalités avancées seront bientôt disponibles :
                statistiques détaillées, alertes, gestion de la qualité, suivi des coûts et rapports.
              </p>
            </div>
          </div>
        </Card>

        <EggProductionStats stockItemId={stockItemId} />

        <Card>
          <CardContent className="p-6">
            <EggProductionList stockItemId={stockItemId} />
          </CardContent>
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enregistrer une production</DialogTitle>
            </DialogHeader>
            <EggProductionForm
              stockItemId={stockItemId}
              onSuccess={() => setIsDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default EggProduction;
